import { type InsuranceBuilder, getActionableInsurances } from '@/types/insurance-builder.type.ts';
import {
  CARRIER_TYPE,
  type CarrierType,
  PLAN_FAMILY,
  type PlanFamily,
  carrierTypeToFamily,
  expandCarrierTypesToPlanFamilies,
} from '@/types/insurance.type.ts';
import type { Product } from '@/types/product.type.ts';
import { isInsuranceEligible } from '@/types/product.type.ts';

type BenefitsProductPartition = {
  coveredCarrierTypes: Set<CarrierType>;
  pendingCarrierTypes: Set<CarrierType>;
  notCoveredCarrierTypes: CarrierType[];
  coveredProducts: Product[];
  pendingProducts: Product[];
  notCoveredProducts: Product[];
};

function getFamilyCoveredByVerified(
  verifiedInsurances: InsuranceBuilder['verifiedInsurances']
): Set<PlanFamily> {
  const families = new Set<PlanFamily>();
  for (const ins of verifiedInsurances) {
    families.add(carrierTypeToFamily(ins.carrier.carrierType));
  }
  return families;
}

/**
 * Same carrier / product bucketing as {@link BenefitsListing}: verified → covered;
 * confirmed upload proof → pending (excluding already-covered SKUs); remaining Medicare/Medicaid →
 * not covered.
 *
 * Also considers `coveredFamilies` - if user confirmed they have coverage for a family
 * (even without verification), that family's base type is not marked as "not covered".
 */
export function partitionProductsByBenefitsCoverage(
  allProducts: Product[],
  builder: InsuranceBuilder
): BenefitsProductPartition {
  const actionableInsurances = getActionableInsurances(builder);
  const actionableVerifiedInsurances = builder.verifiedInsurances.filter((insurance) =>
    actionableInsurances.includes(insurance)
  );
  const actionableConfirmedInsurances = builder.confirmedInsurances.filter((insurance) =>
    actionableInsurances.includes(insurance)
  );
  const coveredCarrierTypes = new Set(
    actionableVerifiedInsurances.map((i) => i.carrier.carrierType)
  );
  const coveredProductEligibilityTypes = expandCarrierTypesToPlanFamilies(coveredCarrierTypes);
  const coveredProducts = allProducts.filter((p) =>
    isInsuranceEligible(p, coveredProductEligibilityTypes)
  );

  const pendingCarrierTypes = new Set(
    actionableConfirmedInsurances.map((i) => i.carrier.carrierType)
  );
  const pendingProductEligibilityTypes = expandCarrierTypesToPlanFamilies(pendingCarrierTypes);
  const pendingProducts = allProducts.filter(
    (product) =>
      isInsuranceEligible(product, pendingProductEligibilityTypes) &&
      coveredProducts.every((covered) => covered.uuid !== product.uuid)
  );

  // Families that are covered (either by verified insurance or user confirmation)
  const familiesCoveredByVerified = getFamilyCoveredByVerified(actionableVerifiedInsurances);
  const allCoveredFamilies = new Set([...builder.coveredFamilies, ...familiesCoveredByVerified]);

  // Only mark as "not covered" if the family isn't covered at all
  const notCoveredCarrierTypes = [
    { type: CARRIER_TYPE.MEDICAID, family: PLAN_FAMILY.MEDICAID },
    { type: CARRIER_TYPE.MEDICARE, family: PLAN_FAMILY.MEDICARE },
  ]
    .filter(
      ({ type, family }) =>
        !coveredCarrierTypes.has(type) &&
        !pendingCarrierTypes.has(type) &&
        !allCoveredFamilies.has(family)
    )
    .map(({ type }) => type);

  const notCoveredCarrierTypeSet = new Set(notCoveredCarrierTypes);
  const notCoveredProducts = allProducts.filter(
    (p) =>
      isInsuranceEligible(p, notCoveredCarrierTypeSet) &&
      coveredProducts.every((covered) => covered.uuid !== p.uuid) &&
      pendingProducts.every((pending) => pending.uuid !== p.uuid)
  );

  return {
    coveredCarrierTypes,
    pendingCarrierTypes,
    notCoveredCarrierTypes,
    coveredProducts,
    pendingProducts,
    notCoveredProducts,
  };
}

/** Catalog rows for supply categories the patient chose on the category step (`ProductCategory.label` ↔ `product.category.name`). */
export function filterProductsToSelectedCategoryLabels(
  products: Product[],
  selectedCategoryLabels: string[]
): Product[] {
  if (selectedCategoryLabels.length === 0) return [];
  return products.filter((p) => selectedCategoryLabels.includes(p.category.name));
}
